// src/components/AdminPortal/Events.js
import { useState, useEffect } from "react";
import { FiEdit, FiTrash2, FiPlusCircle, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import "../ContentManagement.css";
import AxiosWeb from "../../../utils/axiosWeb.js";

const Events = ({ setEventsData, eventsData }) => {
  const [events, setEvents] = useState(eventsData || []);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false); // 🔹 New state for delete popup
  const [eventToDelete, setEventToDelete] = useState(null); // 🔹 To store selected event for delete
  const [newEvent, setNewEvent] = useState(getEmptyEvent());
  const [isLoading, setIsLoading] = useState(false);

  function getEmptyEvent() {
    return {
      title: "",
      description: "",
      file: null,
      fileType: "",
      content: "",
    };
  }

  const updateParent = (updated) => {
    setEvents(updated);
    if (setEventsData) setEventsData(updated);
  };

  const fetchEvents = async () => {
    try {
      setIsLoading(true);
      const response = await AxiosWeb.get(
        `${process.env.REACT_APP_BASE_URL}/events/getAllEvents`
      );
      setEvents(response?.data);
      updateParent(response?.data);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddEvent = () => {
    setNewEvent(getEmptyEvent());
    setEditingIndex(null);
    setShowAddModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewEvent((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const fileType = file.type.startsWith("image")
        ? "image"
        : file.type.startsWith("video")
        ? "video"
        : "";

      setNewEvent((prev) => ({
        ...prev,
        file,
        fileType,
      }));

      const reader = new FileReader();
      reader.onload = (event) => {
        setNewEvent((prev) => ({
          ...prev,
          content: event.target.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveNewEvent = async (e) => {
    e.preventDefault();

    if (!newEvent.title.trim() || !newEvent.description.trim()) {
      toast.error("Title and Description are required");
      return;
    }
    if (!newEvent.file) {
      toast.error("File (Image/Video) is required");
      return;
    }

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("title", newEvent.title);
      formData.append("description", newEvent.description);
      formData.append("content", newEvent.file);

      const response = await AxiosWeb.post(
        `${process.env.REACT_APP_BASE_URL}/events/createEvent`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      const savedEvent = response?.data;
      const updated = [...events, savedEvent];
      updateParent(updated);

      setShowAddModal(false);
      setNewEvent(getEmptyEvent());
      toast.success("Event created successfully!");
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (index) => {
    const event = events[index];
    const fileType = event.contentType?.startsWith("image")
      ? "image"
      : event.contentType?.startsWith("video")
      ? "video"
      : "";
    setEditingIndex(index);
    setNewEvent({
      ...event,
      file: null,
      fileType,
      content: event.contentUrl || "",
    });
    setShowAddModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();

    if (!newEvent.title.trim() || !newEvent.description.trim()) {
      toast.error("Title and Description are required");
      return;
    }

    try {
      setIsLoading(true);
      const eventToUpdate = events[editingIndex];
      const formData = new FormData();
      formData.append("title", newEvent.title);
      formData.append("description", newEvent.description);
      if (newEvent.file) formData.append("content", newEvent.file);

      const response = await AxiosWeb.put(
        `${process.env.REACT_APP_BASE_URL}/events/updateEvent?id=${
          eventToUpdate.id || eventToUpdate._id
        }`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const updatedEvent = response.data;
      const updated = [...events];
      updated[editingIndex] = updatedEvent;
      updateParent(updated);

      setEditingIndex(null);
      setShowAddModal(false);
      setNewEvent(getEmptyEvent());
      toast.success("Event updated successfully!");
      fetchEvents();
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (index) => {
    const selectedEvent = events[index];
    setEventToDelete(selectedEvent);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!eventToDelete) return;
    try {
      setIsLoading(true);
      await AxiosWeb.delete(
        `${process.env.REACT_APP_BASE_URL}/events/deleteEvent?id=${
          eventToDelete.id || eventToDelete._id
        }`
      );
      toast.success("Event deleted successfully!");
      await fetchEvents();
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
      setEventToDelete(null);
    }
  };

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "16px",
        }}
      >
        <button
          onClick={handleAddEvent}
          className="add-new-btn"
          disabled={isLoading}
        >
          <FiPlusCircle size={18} /> Add New Event
        </button>
      </div>

      {events.map((event, index) => (
        <div
          key={event.id || event._id || index}
          className="content-card event-card"
        >
          <div className="content-card-header">
            <h3>Title: {event.title}</h3>
            <div>
              <button
                onClick={() => handleEdit(index)}
                disabled={isLoading}
                className="icon-btn edit-btn"
              >
                <FiEdit size={18} />
              </button>
              <button
                onClick={() => handleDeleteClick(index)}
                disabled={isLoading}
                className="icon-btn delete-btn"
              >
                <FiTrash2 size={18} />
              </button>
            </div>
          </div>
          <div className="content-details-expanded">
            <p>
              <strong>Description:</strong> {event?.description}
            </p>

            {event?.contentType?.startsWith("image") &&
              event?.contentUrl && (
                <img
                  src={event.contentUrl}
                  alt={event?.title}
                  className="event-image"
                />
              )}

            {event?.contentType?.startsWith("video") &&
              event?.contentUrl && (
                <video
                  src={event.contentUrl}
                  controls
                  className="event-video"
                />
              )}
          </div>
        </div>
      ))}

      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingIndex !== null ? "Edit Event" : "Add Event"}</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowAddModal(false)}
                disabled={isLoading}
              >
                <FiX size={24} />
              </button>
            </div>
            <form
              onSubmit={
                editingIndex !== null ? handleSaveEdit : handleSaveNewEvent
              }
              className="edit-form"
            >
              <label>
                Title *
                <input
                  type="text"
                  name="title"
                  value={newEvent.title}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                />
              </label>

              <label>
                Description *
                <textarea
                  name="description"
                  value={newEvent.description}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                />
              </label>

              <label>
                Upload File (Image/Video) *
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileChange}
                  disabled={isLoading}
                />
              </label>

              {newEvent.content && newEvent.fileType === "image" && (
                <img
                  src={newEvent.content}
                  alt="Preview"
                  className="content-image-preview"
                />
              )}
              {newEvent.content && newEvent.fileType === "video" && (
                <video
                  src={newEvent.content}
                  controls
                  className="content-video-preview"
                />
              )}

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowAddModal(false)}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={isLoading}>
                  {isLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-overlay">
          <div
            className="modal-content"
            style={{ maxWidth: "400px", textAlign: "center" }}
          >
            <h3 style={{ color: "#2a4e85", marginBottom: "15px" }}>
              Confirm Delete
            </h3>
            <p style={{ color: "#555", marginBottom: "25px" }}>
              Are you sure you want to delete this event?
            </p>
            <div className="form-actions" style={{ justifyContent: "center" }}>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowDeleteModal(false)}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-save"
                style={{ backgroundColor: "#dc3545" }}
                onClick={confirmDelete}
                disabled={isLoading}
              >
                {isLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
